import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

import type { CycleAudienceCriteriaTypeValue } from '@/constants/cycle-evaluation/CycleAudienceCriteria';
import type {
    CycleCommunicationMilestoneTypeValue,
    CycleCommunicationRecipientTypeValue,
} from '@/constants/cycle-evaluation/CycleCommunication';
import type { CycleAudienceTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluationGroupStepsType } from '@/constants/cycle-evaluation/CycleEvaluationGroupSteps';
import type { FrequencyValue } from '@/constants/Frequency';
import type {
    CycleEvaluationGroup,
    CycleEvaluationGroupStatusValue,
} from '@/types/cycle-evaluation/CycleEvaluationGroup';

import { api, readRefusal } from '@/api/api';
import { queryClient } from '@/AppWithProviders';
import {
    CycleEvaluationGroupQueryKey,
    CycleEvaluationQueryKey,
    CycleMilestoneQueryKey,
} from '@/constants/QueryKeys';

export interface CycleEvaluationGroupPayload {
    name: string;
    evaluationPeriodId?: string | null;
    evaluationPeriodStart?: string | null;
    evaluationPeriodEnd?: string | null;
    currentStep?: CycleEvaluationGroupStepsType;
    communications?: CycleCommunicationBlockPayload[];
    frequency?: FrequencyValue;
    audience?: CycleAudiencePayload;
}

export interface CycleAudienceCriteriaPayload {
    id: string | null;
    criteriaType: CycleAudienceCriteriaTypeValue;
    customAttributeId?: string | null;
    value: string;
}

export interface CycleAudiencePayload {
    cycleAudienceType: CycleAudienceTypeValue;
    cycleAudienceCriterias: CycleAudienceCriteriaPayload[];
}

export interface CycleCommunicationEmailPayload {
    recipient: CycleCommunicationRecipientTypeValue;
    title: Record<string, string>;
    body: Record<string, string>;
    isEnabled: boolean;
}

export interface CycleCommunicationBlockPayload {
    milestoneType: CycleCommunicationMilestoneTypeValue;
    isEnabled: boolean;
    emails: CycleCommunicationEmailPayload[];
}

export interface CycleEvaluationGroupUpdatePayload extends CycleEvaluationGroupPayload {
    status: CycleEvaluationGroupStatusValue;
}

export interface UpdateCycleEvaluationGroupVariables {
    id: string;
    payload: CycleEvaluationGroupUpdatePayload;
}

const CYCLE_EVALUATION_GROUP_NAME_FIELD = 'Name';

export const isDuplicateCycleNameError = (error: unknown): boolean => {
    const refusal = readRefusal(error);

    return (
        refusal?.status === 400 && !!refusal.fieldErrors[CYCLE_EVALUATION_GROUP_NAME_FIELD]?.length
    );
};

export interface CycleEvaluationGroupDeletionConflict {
    submittedEvaluationsCount: number;
    generatedEvaluationsCount: number;
}

export interface CycleEvaluationGroupRemindersSent {
    remindedCount: number;
}

interface DeletionConflictResponse {
    submittedEvaluationsCount?: number;
    generatedEvaluationsCount?: number;
}

export const getCycleEvaluationGroupDeletionConflict = (
    error: unknown,
): CycleEvaluationGroupDeletionConflict | undefined => {
    if (!axios.isAxiosError(error) || error.response?.status !== 409) {
        return undefined;
    }

    const conflict = error.response.data as DeletionConflictResponse | undefined;

    if (
        typeof conflict?.submittedEvaluationsCount !== 'number' ||
        typeof conflict?.generatedEvaluationsCount !== 'number'
    ) {
        return undefined;
    }

    return {
        submittedEvaluationsCount: conflict.submittedEvaluationsCount,
        generatedEvaluationsCount: conflict.generatedEvaluationsCount,
    };
};

const cycleEvaluationGroupMutationService = {
    async createCycleEvaluationGroup(
        payload: CycleEvaluationGroupPayload,
    ): Promise<CycleEvaluationGroup> {
        const response = await api.post('/cycleevaluationgroup', payload);

        return response.data;
    },

    async updateCycleEvaluationGroup({
        id,
        payload,
    }: UpdateCycleEvaluationGroupVariables): Promise<CycleEvaluationGroup> {
        const response = await api.put(`/cycleevaluationgroup/${id}`, payload);

        return response.data;
    },

    async deleteCycleEvaluationGroup(id: string): Promise<CycleEvaluationGroup> {
        const response = await api.delete(`/cycleevaluationgroup/${id}`);

        return response.data;
    },

    async applyCycleEvaluationGroupChanges(id: string): Promise<CycleEvaluationGroup> {
        const response = await api.post(`/cycleevaluationgroup/${id}/apply-changes`);

        return response.data;
    },

    async sendCycleEvaluationGroupReminders(
        id: string,
    ): Promise<CycleEvaluationGroupRemindersSent> {
        const response = await api.post(`/cycleevaluationgroup/${id}/send-reminders`);

        return response.data;
    },
};

const invalidateQueryKeys = (queryKeys: string[]) =>
    Promise.all(
        queryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey: [queryKey] })),
    );

const invalidateCycleEvaluationGroup = () =>
    invalidateQueryKeys([CycleEvaluationGroupQueryKey.CYCLE_EVALUATION_GROUP]);

const invalidateCycleEvaluationGroupAndGeneratedCycles = () =>
    invalidateQueryKeys([
        CycleEvaluationGroupQueryKey.CYCLE_EVALUATION_GROUP,
        CycleEvaluationQueryKey.CYCLE_EVALUATION_LIST,
    ]);

const invalidateAppliedChanges = () =>
    invalidateQueryKeys([
        CycleEvaluationGroupQueryKey.CYCLE_EVALUATION_GROUP,
        CycleMilestoneQueryKey.CYCLE_MILESTONE,
        CycleEvaluationQueryKey.CYCLE_EVALUATION,
    ]);

export const useCreateCycleEvaluationGroup = () =>
    useMutation<CycleEvaluationGroup, Error, CycleEvaluationGroupPayload>({
        mutationFn: cycleEvaluationGroupMutationService.createCycleEvaluationGroup,
        onSuccess: invalidateCycleEvaluationGroup,
    });

export const useUpdateCycleEvaluationGroup = () =>
    useMutation<CycleEvaluationGroup, Error, UpdateCycleEvaluationGroupVariables>({
        mutationFn: cycleEvaluationGroupMutationService.updateCycleEvaluationGroup,
        onSuccess: invalidateCycleEvaluationGroup,
    });

export const useDeleteCycleEvaluationGroup = () =>
    useMutation<CycleEvaluationGroup, Error, string>({
        mutationFn: cycleEvaluationGroupMutationService.deleteCycleEvaluationGroup,
        onSuccess: invalidateCycleEvaluationGroupAndGeneratedCycles,
    });

export const useApplyCycleEvaluationGroupChanges = () =>
    useMutation<CycleEvaluationGroup, Error, string>({
        mutationFn: cycleEvaluationGroupMutationService.applyCycleEvaluationGroupChanges,
        onSuccess: invalidateAppliedChanges,
    });

export const useSendCycleEvaluationGroupReminders = () =>
    useMutation<CycleEvaluationGroupRemindersSent, Error, string>({
        mutationFn: cycleEvaluationGroupMutationService.sendCycleEvaluationGroupReminders,
        onSuccess: invalidateCycleEvaluationGroup,
    });

export default cycleEvaluationGroupMutationService;
